import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, ShieldAlert, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface SafetyConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  operationType: 'DELETE' | 'DROP' | 'TRUNCATE' | 'ALTER';
  estimatedImpact?: {
    affectedRows?: number;
    affectedTables?: string[];
  };
  onConfirm: () => void;
}

const OPERATION_INFO = {
  DELETE: {
    title: 'Delete Operation',
    description: 'This will permanently remove data from your database',
    severity: 'high' as const,
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-900',
  },
  DROP: {
    title: 'Drop Operation',
    description: 'This will permanently delete table(s) and all their data',
    severity: 'critical' as const,
    icon: ShieldAlert,
    color: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    borderColor: 'border-red-200 dark:border-red-900',
  },
  TRUNCATE: {
    title: 'Truncate Operation',
    description: 'This will remove all data from table(s) but keep the structure',
    severity: 'high' as const,
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-200 dark:border-orange-900',
  },
  ALTER: {
    title: 'Alter Operation',
    description: 'This will modify the database structure',
    severity: 'medium' as const,
    icon: Info,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950/20',
    borderColor: 'border-yellow-200 dark:border-yellow-900',
  },
};

export function SafetyConfirmationDialog({
  open,
  onOpenChange,
  query,
  operationType,
  estimatedImpact,
  onConfirm,
}: SafetyConfirmationDialogProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [understand, setUnderstand] = useState(false);

  const info = OPERATION_INFO[operationType];
  const Icon = info.icon;

  const handleConfirm = () => {
    if (confirmed && understand) {
      onConfirm();
      onOpenChange(false);
      setConfirmed(false);
      setUnderstand(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setConfirmed(false);
    setUnderstand(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${info.color}`} />
            {info.title}
          </DialogTitle>
          <DialogDescription>{info.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card className={`${info.borderColor} ${info.bgColor}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Icon className={`h-5 w-5 ${info.color} mt-0.5 shrink-0`} />
                <div className="space-y-2 flex-1">
                  <p className="text-sm font-medium">Destructive Operation Detected</p>
                  <p className="text-sm text-muted-foreground">
                    This query will make permanent changes to your database.
                    {info.severity === 'critical' && ' This action cannot be undone.'}
                  </p>
                </div>
                <Badge
                  variant={
                    info.severity === 'critical'
                      ? 'destructive'
                      : info.severity === 'high'
                      ? 'default'
                      : 'secondary'
                  }
                  className="shrink-0"
                >
                  {info.severity}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {estimatedImpact && (
            <Card>
              <CardContent className="pt-6">
                <h4 className="text-sm font-semibold mb-3">Estimated Impact</h4>
                <div className="space-y-2 text-sm">
                  {estimatedImpact.affectedRows !== undefined && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-muted-foreground">Affected Rows</span>
                      <span className="font-mono font-semibold">
                        {estimatedImpact.affectedRows.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {estimatedImpact.affectedTables && estimatedImpact.affectedTables.length > 0 && (
                    <div className="flex items-start justify-between py-2">
                      <span className="text-muted-foreground">Affected Tables</span>
                      <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                        {estimatedImpact.affectedTables.map((table) => (
                          <Badge key={table} variant="outline" className="font-mono text-xs">
                            {table}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <h4 className="text-sm font-semibold mb-3">Query to Execute</h4>
              <div className="rounded-md bg-muted p-3">
                <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                  {query}
                </pre>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-sm font-medium">Safety Checks</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="understand"
                  checked={understand}
                  onCheckedChange={(checked) => setUnderstand(checked === true)}
                />
                <label
                  htmlFor="understand"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  I understand this operation will modify my database and the changes may be
                  irreversible
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="confirm"
                  checked={confirmed}
                  onCheckedChange={(checked) => setConfirmed(checked === true)}
                />
                <label
                  htmlFor="confirm"
                  className="text-sm leading-relaxed cursor-pointer"
                >
                  I have reviewed the query and want to proceed with this operation
                </label>
              </div>
            </div>
          </div>

          {info.severity === 'critical' && (
            <Card className="border-red-200 dark:border-red-900">
              <CardContent className="pt-4">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Tip:</strong> Consider backing up your data before proceeding with
                  this operation. You can export your database or create a snapshot if your hosting
                  provider supports it.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant={info.severity === 'critical' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={!confirmed || !understand}
          >
            {info.severity === 'critical' ? 'Yes, Proceed Anyway' : 'Confirm & Execute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
